import React, { useState, useRef, Fragment } from "react";
import Emojies from "./EmojiesList";
import useClickOutside from "../../hooks/Screen/useClickOutside";

const EmojiPicker = ({ textareaRef }) => {
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchEmojis, setSearchEmojis] = useState([]);
  const scrollContainerRef = useRef(null);

  const groups = [...new Set(Emojies.map((emoji) => emoji.group))];
  const [selectedGroup, setSelectedGroup] = useState(groups[0]);

  const duplicateEmojies = [...Emojies];
  duplicateEmojies.sort(() => Math.random() - 0.5);
  const limitedEmojies = duplicateEmojies.slice(0, 90);
  const emojisToShow = Emojies.filter((emoji) => emoji.group === selectedGroup);
  const emojiUniqueName = emojisToShow.filter((emoji) => !emoji.name.includes(":"));

  const pickerRef = useClickOutside(() => setEmojiPickerOpen(false));

  const insertEmoji = (emoji) => {
    textareaRef.current.focus();
    document.execCommand("insertText", false, emoji);
  };

  const handleSearchChange = (event) => {
    const query = event.target.value?.toLowerCase();
    setSearchQuery(query);
    const filtered = query.length > 0 ? Emojies.filter((emoji) => emoji.name?.toLowerCase()?.includes(query)) : [];
    setSearchEmojis(filtered);
  };

  const handleScroll = (direction) => {
    if (scrollContainerRef.current) {
      const scrollAmount = 100;
      const currentScroll = scrollContainerRef.current.scrollLeft;
      scrollContainerRef.current.scrollTo({
        left: currentScroll + (direction === "left" ? -scrollAmount : scrollAmount),
        behavior: "smooth",
      });
    }
  };
  const handleClick = () => {
    setEmojiPickerOpen((prev) => !prev);
  };

  return (
    <div className="relative" ref={pickerRef}>
      <button type="button" onClick={handleClick} className="relative">
        <i className="bi bi-emoji-smile text-2xl text-8997A0"></i>
      </button>
      {emojiPickerOpen && (
        <div
          // ref={pickerRef}
          className="absolute z-10 w-fit mt-1 left-[-15px] overflow-auto no-scrollbar origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
          role="menu"
          aria-orientation="vertical"
          tabIndex="-1"
        >
          <div className="py-1 fixed max-w-[340px] rounded mt-2 border p-2 bg-gray-500" role="none">
            <div className="flex items-center justify-center w-full flex-wrap h-full">
              {/* Group scrolling container with arrows */}
              <div className="relative w-full flex items-center justify-center">
                {/* Left scroll arrow */}
                <button
                  type="button"
                  onClick={() => handleScroll("left")}
                  className="absolute left-0 top-1/2 -translate-y-1/2 z-10 h-7 w-7 flex items-center justify-center bg-gray-600 hover:bg-gray-700 rounded-full text-white shadow-lg transition-colors"
                  aria-label="Scroll left"
                >
                  <i className="bi bi-chevron-left"></i>
                </button>

                {/* Scrollable groups container */}
                <div
                  ref={scrollContainerRef}
                  className="flex overflow-x-scroll no-scrollbar items-center gap-2 h-full max-w-[87%] my-1 scroll-smooth px-2"
                >
                  <button
                    type="button"
                    className="p-2 bg-1D4469 rounded-full flex-shrink-0"
                    onClick={() => setSelectedGroup("All")}
                  >
                    🔍
                  </button>
                  {groups.map((group) => (
                    <button
                      key={group}
                      type="button"
                      onClick={() => setSelectedGroup(group)}
                      className="p-2 bg-1D4469 rounded-full flex-shrink-0"
                    >
                      {group === "Smileys & Emotion" && "😎"}
                      {group === "People & Body" && "✋"}
                      {group === "Component" && "🕵️‍♂️"}
                      {group === "Animals & Nature" && "🦁"}
                      {group === "Food & Drink" && "🍷"}
                      {group === "Travel & Places" && "🚝"}
                      {group === "Activities" && "🎁"}
                      {group === "Objects" && "🏀"}
                      {group === "Symbols" && "🕉️"}
                      {group === "Flags" && "🏳️"}
                    </button>
                  ))}
                </div>

                {/* Right scroll arrow */}
                <button
                  type="button"
                  onClick={() => handleScroll("right")}
                  className="absolute right-0 top-1/2 -translate-y-1/2 z-10 h-7 w-7 flex items-center justify-center bg-gray-600 hover:bg-gray-700 rounded-full text-white shadow-lg transition-colors"
                  aria-label="Scroll right"
                >
                  <i className="bi bi-chevron-right"></i>
                </button>
              </div>

              {/* Search input */}
              {selectedGroup === "All" && (
                <div className="my-3 flex justify-center w-full">
                  <input
                    type="search"
                    className="w-full px-3 py-1.5 rounded border"
                    placeholder="🔍 Search Your Emoji"
                    value={searchQuery}
                    onChange={handleSearchChange}
                  />
                </div>
              )}

              {/* Emoji grid */}
              <div className="h-[150px] overflow-y-scroll no-scrollbar flex flex-wrap justify-between w-full">
                <Fragment>
                  {selectedGroup === "All" &&
                    searchQuery.length <= 0 &&
                    limitedEmojies.map((emoji) => (
                      <button
                        key={emoji.codes}
                        onClick={() => insertEmoji(`${emoji.char}`)}
                        title={emoji.name}
                        className="p-1 hover:bg-gray-400 rounded transition-colors"
                      >
                        {emoji.char}
                      </button>
                    ))}
                </Fragment>
                <Fragment>
                  {searchQuery.length > 0 &&
                    searchEmojis.map((emoji) => (
                      <button
                        key={emoji.codes}
                        onClick={() => insertEmoji(`${emoji.char}`)}
                        title={emoji.name}
                        className="p-1 hover:bg-gray-400 rounded transition-colors"
                      >
                        {emoji.char}
                      </button>
                    ))}
                </Fragment>
                <Fragment>
                  {emojiUniqueName.map((emoji) => (
                    <button
                      type="button"
                      key={emoji.codes}
                      onClick={() => insertEmoji(`${emoji.char}`)}
                      title={emoji.name}
                      className="p-1 hover:bg-gray-400 rounded transition-colors"
                    >
                      {emoji.char}
                    </button>
                  ))}
                </Fragment>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmojiPicker;
